<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue'
import { useNavigation } from '../useNavigation'
import type { Student2Context } from '@/types/student2'
import GamesHelpModal from '~/ui-kit/student/GamesHelpModal.vue'
import GameCard from '~/ui-kit/student/GameCard.vue'

const { getLink: $getLink, gotoOnClick: $gotoOnClick, goto: $goto } = useNavigation()

const props = defineProps<{ context: Student2Context }>()
const unitId = ref(Number(window.location.pathname.split('/').pop()))
const gameHelpModal = useTemplateRef('gameHelpModal')

const activeUnitIndex = computed(() => props.context.course_part.units.findIndex(u => u.id === unitId.value))
const purchasedGames = computed(() => props.context.games.filter(g => g.unlocked && !g.demo))
const notPurchasedGames = computed(() => props.context.games.filter(g => !g.unlocked || g.demo))

const helpGameNames = ['karaoke', 'shadowing', 'videoteacher', 'memory-match'] as const
const showHelp = (name: string) => {
  if ((helpGameNames as readonly string[]).includes(name)) {
    gameHelpModal.value?.$open(name as (typeof helpGameNames)[number])
  }
}
</script>

<template>
  <section class="student-cabinet__page student-games-page">
    <div v-if="!purchasedGames.length && !notPurchasedGames.length" class="student-games-page__placeholder">
      <v-icon class="gray-text" size="96">student-v2/controller-off</v-icon>

      В этом юните нет приложений.

      <v-button mode="outlined primary filled" :href="$getLink({ tab: 'home' })" @click="$gotoOnClick">
        <v-icon size="24" class="mr-1">logo-small</v-icon>
        Главная страница
      </v-button>
    </div>

    <GamesHelpModal ref="gameHelpModal" />

    <template v-if="activeUnitIndex !== 0 && purchasedGames.length">
      <h3 class="d-flex games__title mb-1 mb-d-2 mt-4 mt-d-3">Приложения</h3>

      <div class="games mt-2 mb-6">
        <GameCard
          v-for="game in purchasedGames"
          :key="game.id"
          :friendly_name="game.friendly_name"
          :cover_image="game.cover_image"
          :unlocked="game.unlocked"
          :lock_reason="game.lock_reason"
          :draft="game.draft"
          :demo="game.demo"
          @click="$goto($getLink({ hash: `game=${game.id}` }))"
          @show-help="() => showHelp(game.name)"
        />
      </div>
    </template>

    <h3 v-if="activeUnitIndex === 0 || notPurchasedGames.length" class="d-flex games__title mb-1 mb-d-2 mt-4 mt-d-3">
      <template v-if="activeUnitIndex === 0">Приложения</template>
      <template v-else>Магазин приложений</template>
    </h3>

    <template v-if="activeUnitIndex === 0">
      <p class="games__description mb-2">
        В первом юните можно попробовать бесплатно любое приложение - тебе будет доступен только один уровень.
      </p>

      <div class="games mt-2">
        <GameCard
          v-for="game in context.games"
          :key="game.id"
          :friendly_name="game.friendly_name"
          :cover_image="game.cover_image"
          :price="game.price"
          :unlocked="game.unlocked"
          :lock_reason="game.lock_reason"
          :draft="game.draft"
          :demo="game.demo"
          @click="$goto($getLink({ hash: `game=${game.id}` }))"
          @show-help="() => showHelp(game.name)"
        />
      </div>
    </template>

    <div v-else class="games mt-2">
      <GameCard
        v-for="game in notPurchasedGames"
        :key="game.id"
        :friendly_name="game.friendly_name"
        :cover_image="game.cover_image"
        :price="game.price"
        :unlocked="game.unlocked"
        :lock_reason="game.lock_reason"
        :draft="game.draft"
        :demo="game.demo"
        @click="$goto($getLink({ hash: `game=${game.id}` }))"
        @show-help="() => showHelp(game.name)"
      />
    </div>
  </section>
</template>

<style lang="scss">
.student-games-page {
  padding: 0 max(24px, calc((100% - 920px) / 2));
  padding-top: calc(var(--header_height, 0) + 24px);
  padding-bottom: 64px;

  &__placeholder {
    height: calc(100% - 64px);
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 24px;
    @include text-title;
    flex-direction: column;
  }

  .games {
    display: grid;
    grid-gap: 2 * $grid-gap;

    grid-template-columns: repeat(auto-fill, 28 * $grid-gap);
    row-gap: 4 * $grid-gap;

    &__student-book {
      order: 4;
      min-width: 256px;

      .poster {
        height: 162px;
      }
    }

    &__title {
      @include text-title;
      font-weight: bold;
    }
  }
}
</style>
